// src/components/credit/CreditSignaturePad.tsx
import React, { useRef, useState, useEffect } from 'react';
import { PenTool, Type, RotateCcw, Upload, FileImage, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/Button';

interface CreditSignaturePadProps {
  method: 'draw' | 'type' | 'upload';
  setMethod: (method: 'draw' | 'type' | 'upload') => void;
  signatureData: string;
  setSignatureData: (data: string) => void;
  signatoryName: string;
}

export const CreditSignaturePad: React.FC<CreditSignaturePadProps> = ({
  method,
  setMethod,
  signatureData,
  setSignatureData,
  signatoryName,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string>('');

  // Initialize canvas
  useEffect(() => {
    if (method === 'draw' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.strokeStyle = '#0f5a2e';
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // If existing signatureData is PNG, draw it onto canvas
        if (signatureData && signatureData.startsWith('data:image/png')) {
          const img = new Image();
          img.onload = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
          };
          img.src = signatureData;
        }
      }
    }
  }, [method]);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setIsDrawing(true);
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    ctx.beginPath();
    ctx.moveTo(clientX - rect.left, clientY - rect.top);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    ctx.lineTo(clientX - rect.left, clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      setSignatureData(canvas.toDataURL('image/png'));
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
    setSignatureData('');
    setUploadedFileName('');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (PNG, JPG, or JPEG).');
      return;
    }

    setUploadedFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        // Downscale to max 600px width/height to keep PDF light, crisp, and instant to render
        const maxDim = 600;
        let w = img.width;
        let h = img.height;
        if (w > maxDim || h > maxDim) {
          if (w > h) {
            h = Math.round((h * maxDim) / w);
            w = maxDim;
          } else {
            w = Math.round((w * maxDim) / h);
            h = maxDim;
          }
        }
        const offCanvas = document.createElement('canvas');
        offCanvas.width = w;
        offCanvas.height = h;
        const offCtx = offCanvas.getContext('2d');
        if (offCtx) {
          offCtx.drawImage(img, 0, 0, w, h);
          const compressedData = offCanvas.toDataURL('image/png');
          setSignatureData(compressedData);
        } else {
          setSignatureData(reader.result as string);
        }
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-4">
      {/* Mode Selector Tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-2">
        <button
          type="button"
          onClick={() => {
            setMethod('upload');
            setSignatureData('');
          }}
          className={`flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
            method === 'upload'
              ? 'bg-[#0f5a2e] text-white shadow-sm ring-1 ring-emerald-500/20'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <Upload className="w-3.5 h-3.5" />
          Upload Signature & Stamp
        </button>

        <button
          type="button"
          onClick={() => {
            setMethod('draw');
            setSignatureData('');
          }}
          className={`flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
            method === 'draw'
              ? 'bg-[#0f5a2e] text-white shadow-sm ring-1 ring-emerald-500/20'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <PenTool className="w-3.5 h-3.5" />
          Draw Signature
        </button>

        <button
          type="button"
          onClick={() => {
            setMethod('type');
            setSignatureData(signatoryName);
          }}
          className={`flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
            method === 'type'
              ? 'bg-[#0f5a2e] text-white shadow-sm ring-1 ring-emerald-500/20'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <Type className="w-3.5 h-3.5" />
          Type Signature
        </button>
      </div>

      {method === 'upload' && (
        <div className="space-y-3">
          {/* Instructions Box */}
          <div className="p-3.5 bg-emerald-50/70 border border-emerald-200 rounded-lg text-xs space-y-1.5 text-slate-700">
            <div className="font-bold text-[#0f5a2e] flex items-center gap-1.5">
              <span>Instructions for Signing & Stamping:</span>
            </div>
            <ol className="list-decimal list-inside space-y-1 text-slate-600 text-[11px] leading-relaxed">
              <li>Sign your signature and affix your official company stamp on a clean, blank white sheet of paper.</li>
              <li>Take a clear, well-lit photo with your smartphone or scan the signed document.</li>
              <li>Crop closely around the signature and stamp, then upload the image (PNG or JPG) below.</li>
            </ol>
          </div>

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept="image/png, image/jpeg, image/jpg"
            className="hidden"
          />

          {signatureData && signatureData.startsWith('data:image') ? (
            <div className="p-4 border-2 border-emerald-300 rounded-lg bg-white flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img
                  src={signatureData}
                  alt="Uploaded Signature & Stamp"
                  className="max-h-24 max-w-[220px] object-contain border border-slate-200 p-1 rounded bg-slate-50"
                />
                <div className="text-xs">
                  <div className="font-bold text-slate-900 flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span>Signature & Stamp Loaded</span>
                  </div>
                  <span className="text-slate-500 text-[11px]">{uploadedFileName || 'Image attached'}</span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-xs"
                >
                  Replace Image
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={clearCanvas}
                  className="text-xs text-red-600 hover:text-red-700"
                >
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-slate-300 hover:border-[#0f5a2e] bg-slate-50 hover:bg-emerald-50/20 rounded-lg p-6 text-center cursor-pointer transition-colors"
            >
              <FileImage className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <div className="text-xs font-bold text-slate-800">
                Click here to browse and upload your Signature & Stamp image
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Supports PNG, JPG, or JPEG format (Max 5MB)
              </div>
            </div>
          )}
        </div>
      )}

      {method === 'draw' ? (
        <div className="space-y-2">
          <div className="relative border-2 border-dashed border-slate-300 rounded-lg bg-white overflow-hidden touch-none">
            <canvas
              ref={canvasRef}
              width={500}
              height={140}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              className="w-full h-[140px] cursor-crosshair"
            />
            <div className="absolute bottom-2 left-3 pointer-events-none text-[11px] text-slate-400">
              Sign above using mouse, stylus, or fingertip
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-slate-500">Biometric electronic signature</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={clearCanvas}
              className="flex items-center gap-1.5 text-xs text-slate-600"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Clear & Redraw
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="p-4 border-2 border-dashed border-slate-300 rounded-lg bg-slate-50">
            <div className="text-2xl font-serif italic text-[#0f5a2e] tracking-wide select-none">
              {signatoryName || 'Authorized Signatory Full Name'}
            </div>
            <div className="mt-2 text-[11px] text-slate-500">
              By typing your name, you certify this acts as your legally binding electronic signature.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
